import * as React from "react";
import { Avatar, OutlookHeader, SectionLabel } from "./components";
import { fetchContact } from "./api/client";
import { Icon } from "./icons";
import { contactWebUrl } from "../auth/config";
import type { ContactDetailMock, ContactFieldEntry } from "./fixtures";
import type { GqlContact } from "./api/types";
import { useSession } from "./SessionContext";
import type { ContactEntity } from "./types";

/* global Office, window */

/**
 * Open a URL in the user's default browser. Uses Office's openBrowserWindow
 * so it works uniformly across Outlook on the web / Mac / Windows / mobile
 * (which don't all honor a plain target=_blank from inside a task-pane
 * iframe). Falls back to window.open if Office.js isn't available.
 */
function openExternal(url: string): void {
  if (typeof Office !== "undefined" && Office.context?.ui?.openBrowserWindow) {
    Office.context.ui.openBrowserWindow(url);
    return;
  }
  window.open(url, "_blank", "noopener");
}

/**
 * Recompose a display phone string from the backend's digits-only split parts
 * (Contact E.164 redesign 2026-08-22). The dialable part is "+{country_code}{number}"
 * when a country code is present, else the bare national number; the extension is
 * shown separately and is never part of the dial string. Returns null when the
 * slot has no number, so the caller filters the row out.
 */
function composePhone(
  countryCode: string | null,
  number: string | null,
  extension: string | null
): string | null {
  if (!number) return null;
  const dial = countryCode ? `+${countryCode}${number}` : number;
  return extension ? `${dial} ext. ${extension}` : dial;
}

/** Convert a GraphQL Contact's email_1/2/3 + phones into the existing detail shape. */
function detailFromGqlContact(c: GqlContact, matchedEmail: string): ContactDetailMock {
  const emailSlots = [
    { label: "Personal", value: c.email_1 },
    { label: "Work", value: c.email_2 },
    { label: "Other", value: c.email_3 },
  ];
  const me = matchedEmail.toLowerCase();
  const emails: ContactFieldEntry[] = emailSlots
    .filter((s) => !!s.value)
    .map((s) => ({
      label: s.label,
      value: s.value!,
      matched: s.value!.toLowerCase() === me,
    }));

  const phoneSlots: { label: string; value: string | null; sms: boolean }[] = [
    {
      label: "Mobile",
      value: composePhone(
        c.mobile_phone_country_code,
        c.mobile_phone_number,
        c.mobile_phone_extension
      ),
      sms: c.mobile_phone_can_text,
    },
    {
      label: "Home",
      value: composePhone(
        c.phone_home_1_country_code,
        c.phone_home_1_number,
        c.phone_home_1_extension
      ),
      sms: c.phone_home_1_can_text,
    },
    {
      label: "Home (2nd)",
      value: composePhone(
        c.phone_home_2_country_code,
        c.phone_home_2_number,
        c.phone_home_2_extension
      ),
      sms: c.phone_home_2_can_text,
    },
    {
      label: "Business",
      value: composePhone(
        c.phone_business_1_country_code,
        c.phone_business_1_number,
        c.phone_business_1_extension
      ),
      sms: c.phone_business_1_can_text,
    },
    {
      label: "Business (2nd)",
      value: composePhone(
        c.phone_business_2_country_code,
        c.phone_business_2_number,
        c.phone_business_2_extension
      ),
      sms: c.phone_business_2_can_text,
    },
    {
      label: "Business fax",
      value: composePhone(
        c.phone_business_fax_country_code,
        c.phone_business_fax_number,
        c.phone_business_fax_extension
      ),
      sms: false,
    },
  ];
  const phones: ContactFieldEntry[] = phoneSlots
    .filter((s) => !!s.value)
    .map((s) => ({ label: s.label, value: s.value!, smsCapable: s.sms }));

  return { emails, phones };
}

const isEpicId = (id: string): boolean => !!id && !id.startsWith("live-");

export function ContactDetail({
  contact,
  onBack,
}: {
  contact: ContactEntity;
  onBack: () => void;
}): JSX.Element {
  const session = useSession();
  const willFetch = !!session && contact.inEpic && isEpicId(contact.id);
  const [enriched, setEnriched] = React.useState<GqlContact | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(willFetch);

  React.useEffect(() => {
    if (!willFetch || !session) {
      setEnriched(null);
      setIsLoading(false);
      return;
    }
    setEnriched(null);
    setIsLoading(true);
    let cancelled = false;
    (async () => {
      try {
        const c = await fetchContact(
          contact.id,
          session.accessToken,
          session.refreshSession
        );
        if (cancelled) return;
        if (c) setEnriched(c);
      } catch (e) {
        console.warn("ContactDetail load failed:", e);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [contact.id, willFetch, session?.accessToken]);

  // Detail is built from REAL sources only — no fixtures. When the
  // contact is in EPIC and we've loaded it, we get the full record.
  // Otherwise (non-EPIC contact, or fetch failed / hasn't started),
  // we surface only what Office.js gave us — the email used in this
  // message. No phones (we have none).
  const detail: ContactDetailMock =
    enriched != null
      ? detailFromGqlContact(enriched, contact.email)
      : {
          emails: contact.email
            ? [{ label: "Email", value: contact.email, matched: true }]
            : [],
          phones: [],
        };

  const role: string | undefined =
    enriched?.job_title ?? contact.role ?? undefined;
  const company: string | undefined =
    enriched?.business?.name ??
    contact.company ??
    undefined;

  return (
    <>
      <OutlookHeader
        leadingKind="back"
        title="Contact"
        // subtitle={contact.title} // CLAUDE: No subtitle needed.
        onLeading={onBack}
      />
      <div className="pane">
        {(() => {
          const epicUrl = contact.inEpic ? contactWebUrl(contact.id) : null;
          // Rating badge is now rendered inside the Avatar component itself —
          // shows on every avatar (hero + rows) automatically when the
          // contact has a non-NA rating.
          const heroBody = (
            <>
              <Avatar contact={contact} size="xl" />
              <div className="contact-hero-info">
                <div className="meta-line">
                  {contact.inEpic && contact.ref ? (
                    <span className="ref">{contact.ref}</span>
                  ) : (
                    <span className="not-in-epic">Not in EPIC</span>
                  )}
                   {epicUrl && (
                    <span className="ext-icon" aria-hidden="true">
                      <Icon.External />
                    </span>
                  )}
                </div>
                <h2>
                  <span className="contact-hero-name">{contact.title}</span>
                 
                </h2>
                
                {(role || company) && (
                  <div className="meta-line">
                    {role && <span>{role}</span>}
                    {role && company && <span className="dot" />}
                    {company && <span>{company}</span>}
                  </div>
                )}
              </div>
            </>
          );

          if (epicUrl) {
            return (
              <a
                className="contact-hero is-link"
                href={epicUrl}
                target="_blank"
                rel="noopener noreferrer"
                title="Open in EPIC (new window)"
                aria-label={`Open ${contact.title} in EPIC (new window)`}
                onClick={(e) => {
                  e.preventDefault();
                  openExternal(epicUrl);
                }}
              >
                {heroBody}
              </a>
            );
          }
          return <div className="contact-hero">{heroBody}</div>;
        })()}

        {isLoading ? (
          // We only know the contact has at least one email — phones
          // aren't guaranteed. Skeleton only the Email card so we don't
          // promise rows that may never materialize.
          <>
            <SectionLabel title="Email" />
            <div className="card">
              <SkeletonField />
            </div>
          </>
        ) : (
          <>
            {detail.emails.length > 0 && (
              <>
                <SectionLabel title="Email" />
                <div className="card">
                  {detail.emails.map((e, i) => (
                    <button key={i} className="field" type="button">
                      <div className="field-label">
                        <span>{e.label}</span>
                        {e.matched && <span className="field-tag">This email</span>}
                      </div>
                      <div className="field-value">{e.value}</div>
                    </button>
                  ))}
                </div>
              </>
            )}

            {detail.phones.length > 0 && (
              <>
                <SectionLabel title="Phone" />
                <div className="card">
                  {detail.phones.map((p, i) => (
                    <button key={i} className="field" type="button">
                      <div className="field-label">
                        <span>{p.label}</span>
                        {p.smsCapable && <span className="field-tag sms">SMS</span>}
                      </div>
                      <div className="field-value">{p.value}</div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </>
        )}

      </div>
    </>
  );
}

/** Placeholder row matching the .field structure — shown while fetchContact is in flight. */
function SkeletonField(): JSX.Element {
  return (
    <div className="field skeleton" aria-hidden="true">
      <div className="field-label">
        <span className="skel-bar skel-label" />
      </div>
      <span className="skel-bar skel-value" />
    </div>
  );
}
